// ../utils/dist/index.js
function Ok(value) {
  return { ok: true, value };
}
function Err(value) {
  return { ok: false, value };
}
async function wrapErrorAsync(getValue) {
  try {
    return Ok(await getValue());
  } catch (e) {
    return Err(e);
  }
}
function tinyassert(value, message) {
  if (value) {
    return;
  }
  if (message instanceof Error) {
    throw message;
  }
  throw new TinyAssertionError(message, tinyassert);
}
var TinyAssertionError = class _TinyAssertionError extends Error {
  constructor(message, stackStartFunction) {
    super(message);
    if ("captureStackTrace" in Error) {
      Error.captureStackTrace(this, stackStartFunction ?? _TinyAssertionError);
    }
  }
};
function objectHas(v, prop) {
  return Boolean(v && typeof v === "object" && prop in v);
}
function newPromiseWithResolvers() {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

// src/core.ts
function exposeTinyRpc({
  routes,
  adapter
}) {
  return adapter.register(async ({ path, args }) => {
    const fn = objectHas(routes, path) && routes[path];
    tinyassert(
      typeof fn === "function",
      new TinyRpcError("invalid path", { cause: path })
    );
    return fn.apply(routes, args);
  });
}
function proxyTinyRpc({
  adapter
}) {
  return new Proxy(
    {},
    {
      get(_target, path, _receiver) {
        tinyassert(
          typeof path === "string",
          new TinyRpcError("invalid path", { cause: path })
        );
        return async (...args) => {
          try {
            return await adapter.send({ path, args });
          } catch (e) {
            throw TinyRpcError.fromUnknown(e);
          }
        };
      }
    }
  );
}
var TinyRpcError = class _TinyRpcError extends Error {
  // employ http status convention at the core level
  status = 500;
  // convenient api for assertion
  setStatus(status) {
    this.status = status;
    return this;
  }
  serialize() {
    return {
      message: this.message,
      stack: this.stack ?? "",
      cause: this.cause ?? null,
      status: this.status
    };
  }
  static deserialize(e) {
    tinyassert(objectHas(e, "message") && typeof e.message === "string");
    tinyassert(objectHas(e, "stack") && typeof e.stack === "string");
    tinyassert(objectHas(e, "cause"));
    tinyassert(objectHas(e, "status") && typeof e.status === "number");
    return Object.assign(new _TinyRpcError(), e);
  }
  static fromUnknown(e) {
    if (e instanceof _TinyRpcError) {
      return e;
    }
    const err = new _TinyRpcError("unknown", { cause: e });
    if (objectHas(e, "message") && typeof e.message === "string") {
      err.message = e.message;
    }
    return err;
  }
};

// src/adapter-http.ts
function httpServerAdapter(opts) {
  const JSON2 = {
    parse: globalThis.JSON.parse,
    stringify: globalThis.JSON.stringify,
    ...opts.JSON
  };
  return {
    register: (invokeRoute) => {
      return async ({ request }) => {
        const url = new URL(request.url);
        if (!url.pathname.startsWith(opts.endpoint)) {
          return;
        }
        const result = await wrapErrorAsync(async () => {
          const path = url.pathname.slice(opts.endpoint.length + 1);
          const method = opts.pathsForGET?.includes(path) ? "GET" : "POST";
          tinyassert(
            request.method === method,
            new TinyRpcError("invalid method", {
              cause: request.method
            }).setStatus(405)
          );
          let args;
          if (method === "GET") {
            const payload = url.searchParams.get(GET_PAYLOAD_PARAM);
            tinyassert(typeof payload === "string");
            args = JSON2.parse(payload);
          } else {
            args = JSON2.parse(await request.text());
          }
          return invokeRoute({ path, args });
        });
        let status = 200;
        if (!result.ok) {
          opts.onError?.(result.value);
          const e = TinyRpcError.fromUnknown(result.value);
          status = e.status;
          result.value = e.serialize();
        }
        return new Response(JSON2.stringify(result), {
          status,
          headers: {
            "content-type": "application/json; charset=utf-8"
          }
        });
      };
    }
  };
}
function httpClientAdapter(opts) {
  const fetch = opts.fetch ?? globalThis.fetch;
  const JSON2 = {
    parse: globalThis.JSON.parse,
    stringify: globalThis.JSON.stringify,
    ...opts.JSON
  };
  return {
    send: async (data) => {
      const url = [opts.url, data.path].join("/");
      const payload = JSON2.stringify(data.args);
      const method = opts.pathsForGET?.includes(data.path) ? "GET" : "POST";
      let req;
      if (method === "GET") {
        req = new Request(
          url + "?" + new URLSearchParams({ [GET_PAYLOAD_PARAM]: payload })
        );
      } else {
        req = new Request(url, {
          method: "POST",
          body: payload,
          headers: {
            "content-type": "application/json; charset=utf-8"
          }
        });
      }
      const res = await fetch(req);
      const result = JSON2.parse(await res.text());
      if (!result.ok) {
        throw TinyRpcError.deserialize(result.value);
      }
      return result.value;
    }
  };
}
var GET_PAYLOAD_PARAM = "payload";

// src/adapter-message-port.ts
function messagePortServerAdapter({
  port,
  onError
}) {
  return {
    register: (invokeRoute) => {
      return listen(port, async (ev) => {
        const req = ev.data;
        const result = await wrapErrorAsync(async () => invokeRoute(req.data));
        if (!result.ok) {
          onError?.(result.value);
          result.value = TinyRpcError.fromUnknown(result.value).serialize();
        }
        const res = {
          id: req.id,
          result
        };
        port.postMessage(res);
      });
    }
  };
}
function messagePortClientAdapter({
  port,
  generateId = defaultGenerateId
}) {
  return {
    send: async (data) => {
      const req = {
        id: generateId(),
        data
      };
      const promiseResolvers = newPromiseWithResolvers();
      const unlisten = listen(port, (ev) => {
        const res2 = ev.data;
        if (res2.id === req.id) {
          promiseResolvers.resolve(res2);
          unlisten();
        }
      });
      port.postMessage(req);
      const res = await promiseResolvers.promise;
      if (!res.result.ok) {
        throw TinyRpcError.deserialize(res.result.value);
      }
      return res.result.value;
    }
  };
}
function defaultGenerateId() {
  if (typeof globalThis?.crypto?.randomUUID !== "undefined") {
    return globalThis.crypto.randomUUID();
  }
  return mathRandomId();
}
function mathRandomId() {
  return [32, 16, 16, 16, 48].map(
    (bits) => Math.floor(Math.random() * 2 ** bits).toString(16).padStart(bits / 4, "0")
  ).join("-");
}
function listen(port, listener) {
  port.addEventListener("message", listener);
  return () => {
    port.removeEventListener("message", listener);
  };
}

// src/adapter-message-port-node.ts
function messagePortNodeCompat(port) {
  const map = /* @__PURE__ */ new WeakMap();
  return {
    postMessage(data) {
      port.postMessage(data);
    },
    addEventListener(type, handler) {
      const nodeHandler = (data) => handler({ data });
      map.set(handler, nodeHandler);
      port.on(type, nodeHandler);
    },
    removeEventListener(type, handler) {
      const nodeHandler = map.get(handler);
      map.delete(handler);
      tinyassert(nodeHandler);
      port.off(type, nodeHandler);
    }
  };
}

// src/validation.ts
function validateFn(schema) {
  return function decorate(fn) {
    return function wrapper(inputRaw) {
      const parser = getParser(schema);
      let input;
      try {
        input = parser(inputRaw);
      } catch (e) {
        throw TinyRpcError.fromUnknown(e).setStatus(400);
      }
      return fn(input);
    };
  };
}
function getParser(schema) {
  tinyassert(schema && typeof schema === "object");
  if ("parse" in schema && typeof schema.parse === "function") {
    return schema.parse;
  }
  throw new TinyRpcError("unsupported schema", { cause: schema });
}
export {
  TinyRpcError,
  exposeTinyRpc,
  httpClientAdapter,
  httpServerAdapter,
  messagePortClientAdapter,
  messagePortNodeCompat,
  messagePortServerAdapter,
  proxyTinyRpc,
  validateFn
};
